/* Free-shipping amounts in the shopper's own currency.

   Every shipping rate and free-shipping condition in Settings > Shipping is in
   EUR, including the CZ/HU/PL/RO zones. Checkout converts them at Shopify's
   live rate, which the storefront exposes as Shopify.currency.rate, so the
   real threshold in Kc/Ft/zl/lei moves a little every day. Liquid has no access
   to that rate: it can only quote "85 EUR" or a hardcoded guess, and the guesses
   drifted far enough that the cart told Polish and Romanian shoppers shipping
   was free while checkout still charged it.

   The theme therefore prints EUR minor units on the elements below, and this
   script converts them at the same rate checkout uses:

     [data-gb-fx-eur]        a promise ("free over X"): the converted amount
                             rounded UP to a clean number, so the claim stays
                             true whichever way the rate moves within the step
     [data-gb-fx-bar]        the cart drawer progress bar (gb-cart-progress):
                             exact thresholds, so "unlocked" flips at the same
                             cent checkout makes shipping free
     [data-gb-fx-free-row]   the cart's "Standard: Free" row (cart-summary)

   The server-rendered values stay as the no-JS fallback. Nothing runs in a EUR
   market. Cart sections re-render by morphing, which rewrites these nodes back
   to the server values, so a MutationObserver re-applies after every change;
   writes are skipped when the value is already right, which is what stops the
   observer feeding itself. */
import { formatMoney } from '@theme/money-formatting';

const cfg = window.GBFX || {};
const cur = window.Shopify && window.Shopify.currency;
const rate = cur ? parseFloat(cur.rate) : NaN;
const code = cur && cur.active;

function money(minor) {
  const s = formatMoney(minor, cfg.format, code);
  // money_without_trailing_zeros, as the cart prints its own amounts
  return minor % 100 === 0 ? s.replace(/[.,]00(?=\D*$)/, '') : s;
}

// EUR minor units -> presentment minor units, never below the real threshold
function exact(eurMinor) {
  return Math.ceil(eurMinor * rate);
}

// Round up to two significant digits: 2,075 -> 2,100; 31,173 -> 32,000;
// 372 -> 380. Whole units only.
function promise(eurMinor) {
  const major = Math.ceil(exact(eurMinor) / 100);
  const step = Math.pow(10, Math.max(String(major).length - 2, 0));
  return Math.ceil(major / step) * step * 100;
}

function setText(el, text) {
  if (el.textContent !== text) el.textContent = text;
}
// Compare in the browser's own serialisation ("&#10003;" reads back as a
// literal check mark), or the observer would rewrite the node forever.
const scratch = document.createElement('template');
function setHTML(el, html) {
  scratch.innerHTML = html;
  if (el.innerHTML !== scratch.innerHTML) el.innerHTML = html;
}
function setWidth(el, pct) {
  const w = pct + '%';
  if (el && el.style.width !== w) el.style.width = w;
}
function int(el, name) {
  return parseInt(el.getAttribute(name), 10) || 0;
}

function promises() {
  document.querySelectorAll('[data-gb-fx-eur]').forEach((el) => {
    setText(el, money(promise(int(el, 'data-gb-fx-eur'))));
  });
}

function bars() {
  document.querySelectorAll('[data-gb-fx-bar]').forEach((bar) => {
    const total = int(bar, 'data-gb-fx-total');
    const stdEur = int(bar, 'data-gb-fx-std');
    const std = stdEur > 0 ? exact(stdEur) : 0;
    const fast = exact(int(bar, 'data-gb-fx-fast'));
    const mark = Math.round((std * 10000) / fast) / 100;
    const stdPct = std > 0 ? Math.min(100, Math.round((total * 100) / std)) : 0;
    const fastPct = Math.min(100, Math.round((Math.max(total - std, 0) * 100) / (fast - std)));

    setWidth(bar.querySelector('.gb-fs__seg'), mark);
    setWidth(bar.querySelector('.gb-fs__fill'), stdPct);
    const fastFill = bar.querySelector('.gb-fs__fill--fast');
    setWidth(fastFill, fastPct);
    if (fastFill) fastFill.classList.toggle('gb-fs__fill--done', total >= fast);

    const msg = bar.querySelector('.gb-fs__msg');
    if (!msg) return;
    if (total < std) {
      setHTML(msg, bar.getAttribute('data-gb-fx-msg-std').replace('__AMOUNT__', money(std - total)));
    } else if (total < fast) {
      setHTML(msg, bar.getAttribute('data-gb-fx-msg-fast').replace('__AMOUNT__', money(fast - total)));
    } else {
      setHTML(msg, bar.getAttribute('data-gb-fx-msg-done'));
    }
  });
}

function freeRows() {
  document.querySelectorAll('[data-gb-fx-free-row]').forEach((row) => {
    const total = int(row, 'data-gb-fx-total');
    const stdEur = int(row, 'data-gb-fx-std');
    const std = stdEur > 0 ? exact(stdEur) : 0;
    const fast = exact(int(row, 'data-gb-fx-fast'));
    const show = total >= std;
    if (row.hidden === show) row.hidden = !show;
    const label = row.querySelector('.gb-ship-free__label');
    if (label) {
      setText(label, row.getAttribute(total >= fast ? 'data-gb-fx-label-fast' : 'data-gb-fx-label-std') + ':');
    }
  });
}

function apply() {
  promises();
  bars();
  freeRows();
}

const SELECTOR = '[data-gb-fx-eur], [data-gb-fx-bar], [data-gb-fx-free-row]';

// Only mutations in or around our own elements matter; the rest of the page
// (carousels, marquee clones, the header) mutates constantly.
function touchesUs(records) {
  return records.some((r) => {
    const el = r.target.nodeType === 1 ? r.target : r.target.parentElement;
    if (el && el.closest(SELECTOR)) return true;
    return Array.from(r.addedNodes).some(
      (n) => n.nodeType === 1 && (n.matches(SELECTOR) || n.querySelector(SELECTOR))
    );
  });
}

if (cfg.format && code && code !== cfg.shopCurrency && rate > 0) {
  apply();
  let queued = false;
  new MutationObserver((records) => {
    if (queued || !touchesUs(records)) return;
    queued = true;
    queueMicrotask(() => {
      queued = false;
      apply();
    });
  }).observe(document.documentElement, {
    childList: true,
    characterData: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-gb-fx-total', 'style', 'class', 'hidden'],
  });
}
